/*
 * Product form (Kt) — the behaviour the 2026 PDP info column adds on top of the theme: running
 * total, bundled add-to-cart, sticky bar.
 */

function ktFormatMoney(cents, format) {
  const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
  const formatString = format || '${{amount}}';
  const match = formatString.match(placeholderRegex);
  if (!match) return formatString;

  function withDelimiters(number, precision, thousands, decimal) {
    if (isNaN(number) || number === null) return '0';
    const fixed = (number / 100.0).toFixed(precision);
    const parts = fixed.split('.');
    const whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
    return whole + (parts[1] ? decimal + parts[1] : '');
  }

  let value;
  switch (match[1]) {
    case 'amount_no_decimals':
      value = withDelimiters(cents, 0, ',', '.');
      break;
    case 'amount_with_comma_separator':
      value = withDelimiters(cents, 2, '.', ',');
      break;
    case 'amount_no_decimals_with_comma_separator':
      value = withDelimiters(cents, 0, '.', ',');
      break;
    default:
      value = withDelimiters(cents, 2, ',', '.');
  }
  return formatString.replace(placeholderRegex, value);
}

const KT_DN_PRICE_EVENT = 'la:dn:product:discount:calculated';

// Derived from the app's percentage rather than its discountedPrice, which is
// exact to a fraction of a penny: Shopify takes a whole number of pence off each
// item, so the running total only agrees with the cart if we round the same way.
function ktItemDiscount(price, percentage) {
  return Math.floor((price * percentage) / 100 + 1e-9);
}

function ktCountryList(value) {
  const raw = Array.isArray(value) ? value.join(',') : String(value || '');

  return raw
    .toUpperCase()
    .split(',')
    .map((code) => code.trim())
    .filter(Boolean);
}

function ktCountryRule(value) {
  const include = [];
  const exclude = [];

  ktCountryList(value).forEach((code) => {
    if (code.startsWith('!') || code.startsWith('-')) {
      const negated = code.slice(1).trim();
      if (negated) exclude.push(negated);
    } else {
      include.push(code);
    }
  });

  return { include, exclude };
}

function ktOfferFit(offer, country) {
  const rule = ktCountryRule(offer && offer.countries);

  if (rule.exclude.includes(country)) return 0;
  if (!rule.include.length) return 1;

  return rule.include.includes(country) ? 2 : 0;
}

function ktTierRows(value) {
  if (Array.isArray(value)) return value;

  return String(value || '').split(',');
}

function ktParseTier(row) {
  if (row && typeof row === 'object') {
    return {
      quantity: parseInt(row.quantity, 10),
      percentage: parseFloat(row.percentage),
    };
  }

  const parts = String(row).split(':');

  return {
    quantity: parseInt(parts[0], 10),
    percentage: parseFloat(String(parts[1]).replace(/[^0-9.]/g, '')),
  };
}

function ktMultibuyTiers(raw, fallbackCountry) {
  if (!raw) return [];

  let offers;
  try {
    offers = JSON.parse(raw);
  } catch (error) {
    console.error('[kt-product-form] multibuy offers could not be read', error);
    return [];
  }
  if (!Array.isArray(offers) || !offers.length) return [];

  const country = ((window.Shopify && window.Shopify.country) || fallbackCountry || '').toUpperCase();

  let offer = null;
  let fit = 0;
  offers.forEach((candidate) => {
    const candidateFit = ktOfferFit(candidate, country);
    if (candidateFit > fit) {
      fit = candidateFit;
      offer = candidate;
    }
  });
  if (!offer) return [];

  return ktTierRows(offer.tiers)
    .map(ktParseTier)
    .filter(
      (tier) =>
        tier.quantity >= 2 && Number.isFinite(tier.percentage) && tier.percentage > 0 && tier.percentage < 100
    );
}

class KtProductForm extends HTMLElement {
  connectedCallback() {
    if (this.wired) {
      if (this.formSubmitListener) {
        // Capture on the document, so this runs before <product-form>'s own
        // submit listener (bound to the form, so unorderable from here). That
        // is what lets the add-ons go in before the theme's add-to-cart runs.
        document.addEventListener('submit', this.formSubmitListener, true);
      }
      return;
    }

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', this.wire.bind(this), { once: true });
    } else {
      this.wire();
    }
  }

  wire() {
    if (this.wired) return;
    this.wired = true;

    this.form = this.dataset.formId ? document.getElementById(this.dataset.formId) : null;
    this.productForm = this.form ? this.form.closest('product-form') : null;
    this.moneyFormat = this.dataset.moneyFormat || '';

    this.basePrice = parseInt(this.dataset.variantPrice, 10) || 0;
    this.available = this.dataset.variantAvailable === 'true';

    this.variantId = parseInt(this.dataset.variantId, 10) || null;
    this.dnUnitPrice = null;

    this.multibuyTiers = ktMultibuyTiers(this.dataset.multibuyOffers, this.dataset.country);

    this.pending = false;
    this.resubmitting = false;

    this.addonInputs = Array.from(this.querySelectorAll('.kt-addon-row__input'));

    this.stickyButton = this.querySelector('.kt-sticky-atc__button');
    this.stickyLabel = this.querySelector('.kt-sticky-atc__label');
    this.soldOutText = (window.variantStrings && window.variantStrings.soldOut) || '';
    this.addToCartText =
      (this.stickyLabel && this.stickyLabel.textContent) ||
      (window.variantStrings && window.variantStrings.addToCart) ||
      '';

    this.submitButton = this.form ? this.form.querySelector('[name="add"]') : null;
    this.submitLabel = this.submitButton ? this.submitButton.querySelector('span') : null;

    const picker = this.querySelector('variant-radios');
    this.chooseText = (picker && picker.dataset.ktUnselectedText) || 'Select a size';
    this.prompted = false;
    this.totalTargets = Array.from(this.querySelectorAll('[data-kt-total]'));
    this.quantityInput = this.querySelector('.kt-quantity__input');

    this.embroideryToggle = this.querySelector('[data-kt-embroidery-price]');

    this.changeListener = () => {
      this.renderTotal();
      this.syncOptionLabels();
      this.syncOptionAvailability();
      this.syncColourLinks(this.chosenVariantTitle);
    };
    this.addEventListener('change', this.changeListener);

    this.wireColourLabel();
    this.wireColourLinks();
    this.wireStickyColumns();

    if (typeof subscribe === 'function') {
      this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
        const variant = event.data && event.data.variant;
        this.basePrice = variant ? variant.price : 0;
        this.available = Boolean(variant && variant.available);
        this.variantId = variant ? variant.id : null;

        this.dnUnitPrice = null;

        this.renderTotal();
        this.syncOptionLabels();
        this.syncColourLinks(variant ? variant.title : this.chosenVariantTitle);
        this.syncOptionAvailability();
        this.notifyDiscountNinja();
      });
    }

    this.bindDiscountNinja();

    if (window.location.search.indexOf('kt-debug') !== -1) this.debugMultibuy();

    this.renderTotal();
    this.syncOptionLabels();
    this.syncOptionAvailability();

    if (!this.form) return;

    this.formSubmitListener = this.onFormSubmit.bind(this);
    document.addEventListener('submit', this.formSubmitListener, true);
  }

  disconnectedCallback() {
    if (this.formSubmitListener) {
      document.removeEventListener('submit', this.formSubmitListener, true);
    }
    if (this.variantChangeUnsubscriber) this.variantChangeUnsubscriber();
    if (this.dnPriceHandler) {
      document.removeEventListener(KT_DN_PRICE_EVENT, this.dnPriceHandler);
    }
    if (this.columnObserver) this.columnObserver.disconnect();
    if (this.stickySyncListener) {
      window.removeEventListener('resize', this.stickySyncListener);
      window.removeEventListener('orientationchange', this.stickySyncListener);
    }
  }

  wireColourLabel() {
    const row = this.querySelector('.kt-linked-colours');
    this.colourLabel = row ? row.querySelector('[data-kt-colour-label]') : null;
    if (!this.colourLabel) return;

    this.chosenColour = this.colourLabel.textContent;

    const preview = (event) => {
      const swatch = event.target.closest('.kt-linked-colours__swatch');
      if (swatch) this.colourLabel.textContent = swatch.dataset.colourLabel;
    };
    const restore = () => {
      this.colourLabel.textContent = this.chosenColour;
    };

    row.addEventListener('pointerover', preview);
    row.addEventListener('pointerleave', restore);
    row.addEventListener('focusin', preview);
    row.addEventListener('focusout', restore);
  }

  wireColourLinks() {
    this.colourLinks = Array.from(this.querySelectorAll('.kt-linked-colours__swatch[data-kt-variants]'));
    if (this.colourLinks.length === 0) return;

    this.colourLinks.forEach((link) => {
      try {
        link.ktVariants = JSON.parse(link.dataset.ktVariants);
      } catch (error) {
        link.ktVariants = null;
      }
    });

    this.syncColourLinks(this.chosenVariantTitle);
  }

  get unselectedOptions() {
    const picker = this.querySelector('variant-radios');
    if (!picker) return [];

    return Array.from(picker.querySelectorAll('.kt-variant-picker__option')).filter(
      (group) => !group.querySelector('.kt-variant-picker__input:checked')
    );
  }

  get chosenVariantTitle() {
    const picker = this.querySelector('variant-radios');
    if (!picker) return '';

    const chosen = Array.from(picker.querySelectorAll('.kt-variant-picker__option')).map((group) => {
      const checked = group.querySelector('.kt-variant-picker__input:checked');
      return checked ? checked.value : '';
    });

    return chosen.length > 0 && chosen.every(Boolean) ? chosen.join(' / ') : '';
  }

  syncColourLinks(variantTitle) {
    if (!this.colourLinks || this.colourLinks.length === 0) return;

    this.colourLinks.forEach((link) => {
      const base = link.dataset.ktUrl || link.getAttribute('href').split('?')[0];
      const entry = link.ktVariants && variantTitle ? link.ktVariants[variantTitle] : null;
      link.href = entry ? `${base}?variant=${entry.id}` : base;

      if (!link.ktVariants || !variantTitle) return;
      const soldOut = !entry || entry.available === false;
      link.classList.toggle('kt-linked-colours__swatch--unavailable', soldOut);

      const state = link.querySelector('[data-kt-swatch-state]');
      if (state) state.textContent = soldOut && this.soldOutText ? ` (${this.soldOutText})` : '';
    });
  }

  syncOptionLabels() {
    const picker = this.querySelector('variant-radios');
    if (!picker) return;

    picker.querySelectorAll('.kt-variant-picker__option').forEach((group) => {
      const label = group.querySelector('.kt-variant-picker__label-value');
      const checked = group.querySelector('.kt-variant-picker__input:checked');
      if (!label || !checked) return;

      label.textContent = checked.value;
      label.classList.remove('kt-variant-picker__label-value--empty');
    });
  }

  syncOptionAvailability() {
    const picker = this.querySelector('variant-radios');
    if (!picker) return;

    if (this.variantData === undefined) {
      const json = picker.querySelector('script[type="application/json"]');
      try {
        this.variantData = JSON.parse(json.textContent);
      } catch (error) {
        this.variantData = null;
      }
    }
    if (!this.variantData) return;

    const groups = Array.from(picker.querySelectorAll('.kt-variant-picker__option'));
    const chosen = groups.map((group) => {
      const checked = group.querySelector('.kt-variant-picker__input:checked');
      return checked ? checked.value : null;
    });

    groups.forEach((group, index) => {
      group.querySelectorAll('.kt-variant-picker__input').forEach((input) => {
        const combinable = this.variantData.some((variant) => {
          if (!variant.available) return false;
          if (variant.options[index] !== input.value) return false;
          return chosen.every(
            (value, other) => other === index || value === null || variant.options[other] === value
          );
        });
        input.classList.toggle('disabled', !combinable);
      });
    });
  }

  wireStickyColumns() {
    const grid = this.querySelector('.product-main');
    this.stickyColumns = grid ? Array.from(grid.children) : [];
    if (this.stickyColumns.length < 2) return;

    this.stickySyncListener = this.syncStickyColumns.bind(this);
    window.addEventListener('resize', this.stickySyncListener);
    window.addEventListener('orientationchange', this.stickySyncListener);

    if ('ResizeObserver' in window) {
      this.columnObserver = new ResizeObserver(this.stickySyncListener);
      this.stickyColumns.forEach((column) => this.columnObserver.observe(column));
    }

    this.syncStickyColumns();
  }

  syncStickyColumns() {
    const header = document.querySelector('sticky-header');
    const gap = 30;
    const clearOfHeader = (header ? header.offsetHeight : 0) + gap;

    this.stickyColumns.forEach((column) => {
      const room = window.innerHeight - column.offsetHeight - gap;
      column.style.setProperty('--kt-sticky-top', `${Math.round(Math.min(clearOfHeader, room))}px`);
    });
  }

  get quantity() {
    const value = parseInt(this.quantityInput ? this.quantityInput.value : '1', 10);
    return value > 0 ? value : 1;
  }

  get unitPrice() {
    return this.dnUnitPrice === null ? this.basePrice : this.dnUnitPrice;
  }

  get productTotal() {
    const packed = this.packedPrice(this.quantity);
    const plain = this.unitPrice * this.quantity;

    if (packed === null || packed >= this.basePrice * this.quantity) return plain;

    return packed;
  }

  packedPrice(units) {
    if (!this.multibuyTiers.length || this.basePrice <= 0 || units < 0) return null;

    // A tier discounts a SET, not a line: "2:20" is 20% off two, so three is one
    // discounted pair plus one at full price. Tiers never combine — each is
    // repeated as often as it fits and the cheapest result wins.
    const setCost = (tier) =>
      tier.quantity * (this.basePrice - ktItemDiscount(this.basePrice, tier.percentage));

    let best = null;

    this.multibuyTiers.forEach((tier) => {
      if (tier.quantity > units) return;

      const sets = Math.floor(units / tier.quantity);
      const remainder = units - sets * tier.quantity;
      const total = sets * setCost(tier) + remainder * this.basePrice;

      if (best === null || total < best) best = total;
    });

    return best;
  }

  get total() {
    let total = this.productTotal;

    this.addonInputs.forEach((input) => {
      if (input.checked) total += (parseInt(input.dataset.addonPrice, 10) || 0) * this.quantity;
    });

    if (this.embroideryToggle && this.embroideryToggle.checked) {
      total += (parseInt(this.embroideryToggle.dataset.ktEmbroideryPrice, 10) || 0) * this.quantity;
    }

    return total;
  }

  renderTotal() {
    const unselected = this.unselectedOptions;
    const needsOption = unselected.length > 0;
    this.classList.toggle('kt-product-form--needs-option', needsOption);

    unselected.forEach((group) => group.classList.toggle('kt-variant-picker__option--needed', this.prompted));
    if (!needsOption && this.prompted) {
      this.prompted = false;
      this.querySelectorAll('.kt-variant-picker__option--needed').forEach((group) => {
        group.classList.remove('kt-variant-picker__option--needed');
      });
      this.hideError();
    }

    const text = this.available && !needsOption ? ktFormatMoney(this.total, this.moneyFormat) : '';
    this.totalTargets.forEach((target) => {
      target.textContent = text;
      target.hidden = text === '';
    });

    if (this.stickyButton) this.stickyButton.disabled = needsOption || !this.available;
    if (this.stickyLabel) {
      if (needsOption) {
        this.stickyLabel.textContent = this.chooseText;
      } else {
        this.stickyLabel.textContent = this.available ? this.addToCartText : this.soldOutText;
      }
    }

    if (this.submitButton && this.submitLabel) {
      if (needsOption) {
        this.submitButton.disabled = true;
        this.submitButton.setAttribute('aria-disabled', 'true');
        this.submitLabel.textContent = this.chooseText;
      } else if (this.submitLabel.textContent === this.chooseText) {
        this.submitLabel.textContent = this.available ? this.addToCartText : this.soldOutText;
      }
    }

    this.classList.toggle('kt-product-form--sold-out', !needsOption && !this.available);
  }

  debugMultibuy() {
    const raw = this.dataset.multibuyOffers;
    const shopifyCountry = (window.Shopify && window.Shopify.country) || null;
    const country = (shopifyCountry || this.dataset.country || '').toUpperCase();

    let offers = null;
    let parseError = null;
    try {
      offers = JSON.parse(raw || 'null');
    } catch (error) {
      parseError = error.message;
    }

    const considered = Array.isArray(offers)
      ? offers.map((offer) => {
          const rule = ktCountryRule(offer && offer.countries);
          return {
            countries: offer && offer.countries,
            countriesReadAs: typeof (offer && offer.countries),
            include: rule.include.join(' ') || '(none)',
            exclude: rule.exclude.join(' ') || '(none)',
            fit: ktOfferFit(offer, country),
            tiers: JSON.stringify(offer && offer.tiers),
          };
        })
      : [];

    const quantity = this.quantity;
    const packed = this.packedPrice(quantity);

    const report = {
      country: { fromShopify: shopifyCountry, fromLiquid: this.dataset.country || null, used: country },
      offers: { raw: raw || '(attribute absent)', parseError, count: considered.length },
      tiersInUse: this.multibuyTiers,
      price: {
        variantId: this.variantId,
        basePrice: this.basePrice,
        discountNinjaUnitPrice: this.dnUnitPrice,
        quantity,
        plain: this.unitPrice * quantity,
        packed,
        shown: ktFormatMoney(this.total, this.moneyFormat),
      },
    };

    console.log('[kt-product-form] multibuy', report);
    if (considered.length) console.table(considered);
    if (!considered.length) {
      console.warn('[kt-product-form] no offers reached the page — check the product\'s multibuy_group and the group\'s offers list');
    } else if (!this.multibuyTiers.length) {
      console.warn(`[kt-product-form] offers reached the page but none apply in ${country || '(no country)'} — see the fit column`);
    }

    return report;
  }

  bindDiscountNinja() {
    this.dnPriceHandler = this.onDiscountNinjaPrice.bind(this);
    document.addEventListener(KT_DN_PRICE_EVENT, this.dnPriceHandler);
  }

  notifyDiscountNinja() {
    if (!this.variantId) return;

    document.dispatchEvent(
      new CustomEvent('la:dn:variant:changed', {
        detail: { data: { variant: { id: this.variantId } } },
      })
    );
  }

  onDiscountNinjaPrice(event) {
    const info = event && event.detail && event.detail.data && event.detail.data[0];
    if (!info) return;

    if (!this.variantId || Number(info.variantId) !== this.variantId) return;

    let unit = NaN;

    if (info.isDiscountedByOffer) {
      const percentage = Number(info.percentage);
      const discountedPrice = Number(info.discountedPrice);

      if (Number.isFinite(percentage) && percentage > 0) {
        unit = this.basePrice - ktItemDiscount(this.basePrice, percentage);
      } else if (Number.isFinite(discountedPrice)) {
        unit = Math.ceil(discountedPrice);
      }
    }

    const next = Number.isFinite(unit) && unit > 0 && unit < this.basePrice ? unit : null;

    if (next === this.dnUnitPrice) return;

    this.dnUnitPrice = next;
    this.renderTotal();
  }

  selectedItems() {
    const group = Date.now().toString();
    const properties = { '_Gifting Group': group, '_Gifting Item': 'true' };
    const quantity = this.quantity;
    const items = [];

    this.addonInputs.forEach((input) => {
      if (!input.checked) return;

      const lineProperties = { ...properties };
      if (input.dataset.addonNamed === 'true') lineProperties['_Gifting Named'] = 'true';

      items.push({ id: Number(input.value), quantity, properties: lineProperties });

      const extras = this.querySelector(`[data-kt-addon-extras-for="${input.id}"]`);
      if (!extras) return;
      try {
        JSON.parse(extras.textContent).forEach((extra) => {
          items.push({ ...extra, quantity, properties: { ...properties } });
        });
      } catch (error) {
        console.error('Kt: could not read add-on extras for', input.id, error);
      }
    });

    return { items, group };
  }

  onFormSubmit(event) {
    if (event.target !== this.form) return;

    if (this.resubmitting) {
      this.resubmitting = false;
      return;
    }

    if (this.pending) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    const unselected = this.unselectedOptions;
    if (unselected.length > 0) {
      event.preventDefault();
      event.stopPropagation();
      this.promptForOptions(unselected);
      return;
    }

    const { items, group } = this.selectedItems();
    if (items.length === 0) return;

    event.preventDefault();
    event.stopPropagation();

    this.pending = true;
    this.setGroupProperty(group);
    this.setLoading(true);

    // cart/add.js always answers in JSON; plain /cart/add can redirect to the cart page, and Safari then shows its raw parse error.
    fetch(`${window.Shopify.routes.root}cart/add.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({ items }),
    })
      .then((response) =>
        response.text().then((text) => {
          let body = null;
          try {
            body = JSON.parse(text);
          } catch (error) {
            console.error('Kt: cart/add did not return JSON', response.status, text.slice(0, 200));
          }
          if (!response.ok || !body) throw new Error((body && (body.description || body.message)) || '');
          return body;
        })
      )
      .then(() => {
        this.pending = false;
        this.resubmitting = true;
        this.form.requestSubmit();
      })
      .catch((error) => {
        console.error(error);
        this.pending = false;
        this.setGroupProperty(null);
        this.setLoading(false);
        this.showError(error.message);
      });
  }

  setGroupProperty(group) {
    const name = 'properties[_Gifting Group]';
    let input = this.form.querySelector(`input[name="${name}"]`);

    if (group === null) {
      if (input) input.remove();
      return;
    }

    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      this.form.appendChild(input);
    }
    input.value = group;
  }

  // aria-disabled is left alone on purpose: <product-form> bails out of its own
  // handler when it finds that set, and this hands straight over to it.
  setLoading(loading) {
    if (!this.productForm) return;
    const button = this.productForm.querySelector('[type="submit"]');
    const spinner = this.productForm.querySelector('.loading-overlay__spinner');

    if (button) button.classList.toggle('loading', loading);
    if (spinner) spinner.classList.toggle('hidden', !loading);
  }

  promptForOptions(groups) {
    this.prompted = true;
    this.showError(this.chooseText);

    const group = groups[0];
    if (!group) return;

    group.classList.add('kt-variant-picker__option--needed');

    const first =
      group.querySelector('.kt-variant-picker__input:not(.disabled)') ||
      group.querySelector('.kt-variant-picker__input');
    if (first) first.focus({ preventScroll: true });

    group.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  hideError() {
    if (!this.productForm) return;
    const wrapper = this.productForm.querySelector('.product-form__error-message-wrapper');
    if (wrapper) wrapper.setAttribute('hidden', '');
  }

  showError(message) {
    if (!this.productForm) return;
    const wrapper = this.productForm.querySelector('.product-form__error-message-wrapper');
    const text = wrapper ? wrapper.querySelector('.product-form__error-message') : null;
    if (!wrapper || !text) return;

    text.textContent = message || this.dataset.errorText || '';
    wrapper.removeAttribute('hidden');
  }
}

class KtProductDescription extends HTMLElement {
  connectedCallback() {
    if (this.wired) return;
    this.wired = true;

    this.body = this.querySelector('.kt-description__body');
    if (!this.body) return;

    this.style.setProperty('--kt-description-lines', parseInt(this.dataset.lines, 10) || 2);

    this.toggle = document.createElement('button');
    this.toggle.type = 'button';
    this.toggle.className = 'kt-description__toggle';
    this.toggle.textContent = this.dataset.moreText || '';
    this.toggle.setAttribute('aria-expanded', 'false');
    this.toggle.addEventListener('click', this.onToggle.bind(this));

    this.collapse();
    this.appendChild(this.toggle);

    this.syncToggle();
    this.resizeObserver = new ResizeObserver(this.syncToggle.bind(this));
    this.resizeObserver.observe(this.body);
  }

  disconnectedCallback() {
    if (this.resizeObserver) this.resizeObserver.disconnect();
  }

  syncToggle() {
    if (this.expanded) return;
    this.toggle.hidden = this.body.scrollHeight - this.body.clientHeight <= 1;
    this.classList.toggle('kt-description--clamped', !this.toggle.hidden);
  }

  collapse() {
    this.expanded = false;
    this.body.classList.add('kt-description__body--clamped');
    this.toggle.textContent = this.dataset.moreText || '';
    this.toggle.setAttribute('aria-expanded', 'false');
    this.classList.add('kt-description--clamped');
  }

  expand() {
    this.expanded = true;
    this.body.classList.remove('kt-description__body--clamped');
    this.toggle.textContent = this.dataset.lessText || '';
    this.toggle.setAttribute('aria-expanded', 'true');
    this.classList.remove('kt-description--clamped');
  }

  get details() {
    if (!this.dataset.details) return null;
    const target = document.getElementById(this.dataset.details);
    return target && target.tagName === 'DETAILS' ? target : null;
  }

  onToggle() {
    const details = this.details;

    if (details) {
      details.open = true;

      const header = document.querySelector('sticky-header');
      const offset = (header ? header.offsetHeight : 0) + 20;
      window.scrollTo({
        top: window.scrollY + details.getBoundingClientRect().top - offset,
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
      });

      const summary = details.querySelector('summary');
      if (summary) summary.focus({ preventScroll: true });

      return;
    }

    if (this.expanded) {
      this.collapse();
      this.syncToggle();
    } else {
      this.expand();
    }
  }
}

class KtStickyAtc extends HTMLElement {
  connectedCallback() {
    if (this.wired) return;
    this.wired = true;

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', this.wire.bind(this), { once: true });
    } else {
      this.wire();
    }
  }

  wire() {
    const watched = this.dataset.watch ? document.querySelector(this.dataset.watch) : null;
    if (!watched) return;

    this.hidden = false;

    this.observer = new IntersectionObserver(
      ([entry]) => {
        const scrolledPast = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        this.classList.toggle('kt-sticky-atc--visible', scrolledPast);
      },
      { threshold: 0 }
    );
    this.observer.observe(watched);
  }

  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
  }
}

if (!customElements.get('kt-product-form')) customElements.define('kt-product-form', KtProductForm);
if (!customElements.get('kt-product-description')) {
  customElements.define('kt-product-description', KtProductDescription);
}
if (!customElements.get('kt-sticky-atc')) customElements.define('kt-sticky-atc', KtStickyAtc);
